import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Star, ExternalLink, Share2, Bookmark, ThumbsUp, ThumbsDown } from 'lucide-react';
import { aiTools } from '../data/aiTools';
import { AITool } from '../types';
import { SEOHead } from './SEOHead';
import { RelatedTools } from './RelatedTools';
import { ToolFeatureComparison } from './ToolFeatureComparison';
import { PricingTable } from './PricingTable';
import { UserReviews } from './UserReviews';

export const ToolDetailPage: React.FC = () => {
  const { toolId } = useParams<{ toolId: string }>();
  const [tool, setTool] = useState<AITool | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedTools, setRelatedTools] = useState<AITool[]>([]);
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    // Simulate API fetch with timeout
    const timer = setTimeout(() => {
      try {
        if (!toolId) {
          setError('Tool ID is missing');
          setIsLoading(false);
          return;
        }

        const foundTool = aiTools.find(t => t.id === toolId);
        if (!foundTool) {
          setError('Tool not found');
          setIsLoading(false);
          return;
        }

        setTool(foundTool);
        
        // Find related tools
        const related = aiTools
          .filter(t => 
            t.id !== toolId && 
            (t.categories.some(cat => foundTool.categories.includes(cat)) || 
             t.useCases.some(uc => foundTool.useCases.includes(uc)))
          )
          .sort((a, b) => b.compatibilityScore - a.compatibilityScore)
          .slice(0, 5);
        
        setRelatedTools(related);
        setIsLoading(false);
      } catch (err) {
        setError('Failed to load tool details');
        setIsLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [toolId]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-6"></div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded mb-6"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-3"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6 mb-3"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/6 mb-6"></div>
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
        </div>
      </div>
    );
  }

  if (error || !tool) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 p-6 rounded-lg">
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p className="mb-4">{error || 'Failed to load tool details'}</p>
          <Link to="/" className="inline-flex items-center text-red-600 dark:text-red-400 hover:underline">
            <ArrowLeft size={16} className="mr-2" />
            Return to home page
          </Link>
        </div>
      </div>
    );
  }

  // Format pricing information
  const getPricingInfo = () => {
    switch (tool.pricingType) {
      case 'free':
        return 'Free';
      case 'paid':
        return 'Paid subscription';
      case 'freemium':
        return 'Free plan available with premium options';
      default:
        return 'Contact for pricing';
    }
  };

  // Generate meta description
  const metaDescription = `Discover ${tool.name} features, pricing, and alternatives. Compare with other AI tools for ${tool.useCases[0].replace('-', ' ')}. Updated ${currentMonth} ${currentYear}.`;

  return (
    <>
      <SEOHead
        title={`${tool.name} - AI Tool Reviews & Alternatives | YourAIToolFinder`}
        description={metaDescription}
        ogImage={tool.logo}
        ogType="product"
        schemaType="tool"
        schemaData={tool}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: tool.categories[0].charAt(0).toUpperCase() + tool.categories[0].slice(1), url: `/category/${tool.categories[0]}` },
          { name: tool.name, url: `/tool/${tool.id}` }
        ]}
      />

      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-6">
          <Link to="/" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
            Home
          </Link>
          <span className="mx-2">/</span>
          <Link 
            to={`/category/${tool.categories[0]}`} 
            className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            {tool.categories[0].charAt(0).toUpperCase() + tool.categories[0].slice(1)}
          </Link>
          <span className="mx-2">/</span>
          <span className="text-gray-900 dark:text-gray-100 font-medium">
            {tool.name}
          </span>
        </nav>

        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 mb-6"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to all tools
        </Link>

        {/* Tool Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700 mb-8">
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center">
              <div className="w-20 h-20 rounded-xl overflow-hidden mr-6 bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4 md:mb-0">
                <img 
                  src={tool.logo} 
                  alt={`${tool.name} logo`} 
                  className="w-full h-full object-cover"
                  loading="eager"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1679083216051-aa510a1a2c0e?q=80&w=100&auto=format&fit=crop';
                  }}
                />
              </div>
              
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{tool.name}</h1>
                  <div className="flex items-center bg-yellow-100 dark:bg-yellow-900/50 px-3 py-1 rounded-full">
                    <Star className="text-yellow-500 mr-1" size={16} />
                    <span className="text-yellow-800 dark:text-yellow-300 font-medium text-sm">{tool.compatibilityScore}% Match</span>
                  </div>
                </div>
                
                <p className="text-gray-700 dark:text-gray-300 text-lg mb-4">{tool.description}</p>
                
                <div className="flex flex-wrap gap-3 mb-6">
                  {tool.useCases.map(useCase => (
                    <span 
                      key={useCase} 
                      className="inline-flex items-center px-3 py-1 rounded-md text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 capitalize"
                    >
                      {useCase.replace('-', ' ')}
                    </span>
                  ))}
                </div>
                
                <div className="flex flex-wrap gap-4">
                  <a
                    href={tool.tryNowLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Try Now <ExternalLink size={16} className="ml-2" />
                  </a>
                  
                  <button className="inline-flex items-center px-4 py-2.5 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors">
                    <Share2 size={16} className="mr-2" />
                    Share
                  </button>
                  
                  <button className="inline-flex items-center px-4 py-2.5 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors">
                    <Bookmark size={16} className="mr-2" />
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview Section */}
            <section className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Overview</h2>
                <div className="prose prose-indigo dark:prose-invert max-w-none">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{tool.overview}</p>
                </div>
              </div>
            </section>
            
            {/* Key Features Section */}
            <section className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Key Features</h2>
                <ul className="space-y-3">
                  {[
                    `Advanced ${tool.useCases[0].replace('-', ' ')} capabilities`,
                    `Suitable for ${tool.experienceLevels.join(', ')} users`,
                    `${tool.pricingType === 'free' ? 'No cost' : tool.pricingType === 'freemium' ? 'Free tier available' : 'Professional-grade tools'}`,
                    'Regular updates and improvements',
                    'Responsive customer support'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-full mr-3 flex-shrink-0">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </span>
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
            
            {/* Getting Started Section */}
            <section className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Getting Started</h2>
                <div className="prose prose-indigo dark:prose-invert max-w-none">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{tool.gettingStarted}</p>
                </div>
              </div>
            </section>
            
            {/* Comparison Table */}
            <ToolFeatureComparison currentTool={tool} competitors={relatedTools.slice(0, 3)} />
            
            {/* User Reviews */}
            <UserReviews toolId={tool.id} />
          </div>
          
          {/* Right Column - Sidebar */}
          <div className="space-y-8">
            {/* Tool Info Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Tool Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Pricing</h4>
                    <p className="text-gray-900 dark:text-gray-100">{getPricingInfo()}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Experience Level</h4>
                    <p className="text-gray-900 dark:text-gray-100 capitalize">{tool.experienceLevels.join(', ')}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Categories</h4>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {tool.categories.map(category => (
                        <Link 
                          key={category}
                          to={`/category/${category}`}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300 capitalize"
                        >
                          {category.replace('-', ' ')}
                        </Link>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Website</h4>
                    <a 
                      href={tool.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      {tool.website.replace(/^https?:\/\/(www\.)?/, '')}
                    </a>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</h4>
                    <p className="text-gray-900 dark:text-gray-100">{currentMonth} {currentYear}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Pricing Table */}
            <PricingTable tool={tool} />
            
            {/* Related Tools */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Alternative AI Tools</h3>
                <div className="space-y-4">
                  {relatedTools.map((relatedTool) => (
                    <Link 
                      key={relatedTool.id}
                      to={`/tool/${relatedTool.id}`}
                      className="flex items-start p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden mr-3 bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                        <img 
                          src={relatedTool.logo} 
                          alt={`${relatedTool.name} logo`} 
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1679083216051-aa510a1a2c0e?q=80&w=100&auto=format&fit=crop';
                          }}
                        />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">{relatedTool.name}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{relatedTool.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Was This Helpful */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="p-6 text-center">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3">Was this helpful?</h3>
                <div className="flex justify-center gap-4">
                  <button className="inline-flex items-center px-4 py-2 bg-green-100 hover:bg-green-200 dark:bg-green-900/50 dark:hover:bg-green-800/50 text-green-800 dark:text-green-300 rounded-lg transition-colors">
                    <ThumbsUp size={16} className="mr-2" />
                    Yes
                  </button>
                  <button className="inline-flex items-center px-4 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900/50 dark:hover:bg-red-800/50 text-red-800 dark:text-red-300 rounded-lg transition-colors">
                    <ThumbsDown size={16} className="mr-2" />
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};